/*******************************************************
* FormSelect Component
* 
* Enhanced autocomplete component that supports both static arrays and API data sources
* with proper loading state handling and visual feedback.
* 
* Key Features:
* - Supports static array data or API-based data fetching
* - Displays loading state with visual feedback
* - Debounced search for performance optimization
********************************************************/
'use client';
//########## IMPORTS ##########
// #region IMPORTS
import { Autocomplete, type AutocompleteRenderInputParams, type AutocompleteProps, type TextFieldProps, type AutocompleteChangeReason, type AutocompleteInputChangeReason, useTheme, useMediaQuery } from '@mui/material';
import { type DatabaseSchema, type SchemaTableName, type SortItem, useAPI } from '@/src/scripts/APIClient';
import { useEffect, useState, useRef, useCallback } from 'react';
import React, { type ReactNode } from 'react';
import type ReactTypes from 'react';
import useGlobalSettings from '@/src/hooks/useGlobalSettings';
import MobileSelectDrawer from './MobileSelectDrawer';
import { matchesFormSelectSearchValue, sortOptionsByLabel } from './formSelectSearchUtils';
// #endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
export interface FormSelectProps<T> extends Omit<AutocompleteProps<T, false, boolean, false>, 'options' | 'loading'> {
    dataSource: T[] | {
        tableName: string;
        schema: string;
        params?: Record<string, string | number> | URLSearchParams;
        depth?: number[];
        distinctColumn?: string;
        /** Server-side sort order. Defaults to ascending on distinctColumn when set. */
        sort?: readonly SortItem[];
        searchColumns: string[];
        /** Server-side text matching operator. Defaults to substring matching. */
        searchOperator?: 'contains' | 'containsall';
        localFilter?: (option: T) => boolean;
    };
    /** Debounce delay in milliseconds for API calls (default: 200ms) */
    debounceMs?: number;
    /** Page size for API calls (default: 100) */
    pageSize?: number;
    /** When true, automatically selects the only available option if exactly one exists */
    autoSelectSingleOption?: boolean;
    /** Optional secondary line shown for each option in the mobile drawer */
    getOptionSecondaryText?: (option: T) => string | null | undefined;
    /** Optional grouping override for the mobile drawer. Defaults to Autocomplete's groupBy prop. */
    mobileGroupBy?: (option: T) => string;
    /** Sort options A-Z by label (default: true). Set false only when the list has a meaningful fixed order. */
    sortOptions?: boolean;
}


//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS

//#region CONSTANT - STYLES

//#endregion
//#endregion


//########## FUNCTIONS ##########
// #region FUNCTIONS
// Creates search parameters for substring (contains) search
function createSearchParams(
    searchColumns: string[],
    inputValue: string,
    searchOperator: 'contains' | 'containsall' = 'contains',
): Record<string, string> {
    if (!inputValue.trim()) return {};

    const searchParams: Record<string, string> = {};
    const searchValue = searchOperator === 'containsall'
        ? inputValue.trim().split(/\s+/).join(',')
        : inputValue;

    searchColumns.forEach((col) => {
        searchParams[`${col}__${searchOperator}`] = searchValue;
    });

    return searchParams;
}
//#endregion

export default function FormSelect<T>({
    dataSource,
    onInputChange,
    debounceMs,
    pageSize,
    autoSelectSingleOption = false,
    getOptionSecondaryText,
    mobileGroupBy,
    sortOptions = true,
    ...props
}: FormSelectProps<T>) {
    //########## MAIN LOGIC ##########
    // #region MAIN LOGIC
    // Initialize inputValue from the current value to preserve state during re-renders
    const getInitialInputValue = () => {
        if (props.value && props.getOptionLabel) {
            return props.getOptionLabel(props.value);
        }
        return '';
    };

    const [inputValue, setInputValue] = useState(getInitialInputValue);
    const [debouncedInputValue, setDebouncedInputValue] = useState('');
    const { getSetting } = useGlobalSettings();
    const [allDataLoaded, setAllDataLoaded] = useState(false);
    // Track whether we've evaluated the initial API response to decide caching behavior
    const initialApiEvaluatedRef = React.useRef<boolean>(false);
    const [cachedData, setCachedData] = useState<T[]>([]);
    const [hasUserTyped, setHasUserTyped] = useState(false);

    // Mobile bottom sheet drawer
    const theme = useTheme();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down('tablet'));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerSearchValue, setDrawerSearchValue] = useState('');
    const drawerLabelRef = useRef<string>('');
    const drawerPlaceholderRef = useRef<string>('');

    // Sync inputValue when external value changes (e.g., during theme changes)
    useEffect(() => {
        if (props.value && props.getOptionLabel) {
            setInputValue(props.getOptionLabel(props.value));
            setHasUserTyped(false);
        } else if (!props.value) {
            setInputValue('');
            setHasUserTyped(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value, props.getOptionLabel]);

    // Reset cache state when dataSource changes (fixes mode switching issue)
    useEffect(() => {
        if (!Array.isArray(dataSource)) {
            setAllDataLoaded(false);
            setCachedData([]);
            initialApiEvaluatedRef.current = false;
        }
    }, [dataSource]);

    // Determine if the dataSource is an array or not
    const dataArray = Array.isArray(dataSource) ? dataSource : null;
    const apiSource = !Array.isArray(dataSource) ? dataSource : null;

    // Debounce input value for API calls
    const resolvedDebounceMs = (() => {
        if (typeof debounceMs === 'number') return debounceMs;
        const globalVal = getSetting('Debounce_Default');
        return typeof globalVal === 'number' ? globalVal : 300;
    })();

    const resolvedPageSize = (() => {
        if (typeof pageSize === 'number') return pageSize;
        const fromGlobal = getSetting('PageSize_FormSelect');
        return typeof fromGlobal === 'number' ? fromGlobal : 100;
    })();

    useEffect(() => {
        if (!apiSource || allDataLoaded) return; // No debouncing needed for static arrays or when all data is loaded

        const timer = setTimeout(() => {
            setDebouncedInputValue(inputValue);
        }, resolvedDebounceMs);

        return () => clearTimeout(timer);
    }, [inputValue, resolvedDebounceMs, apiSource, allDataLoaded]);

    const apiClient = useAPI({
        schema: apiSource?.schema as DatabaseSchema,
        tableName: apiSource?.tableName as SchemaTableName
    });

    // Determine if the current input matches the selected value's label
    // If so, we should NOT use it as a search filter - the user hasn't typed anything new
    const selectedValueLabel = props.value && props.getOptionLabel ? props.getOptionLabel(props.value) : '';
    const isInputMatchingSelection = !!selectedValueLabel && debouncedInputValue === selectedValueLabel;

    // Only apply search params when:
    // 1. We have search columns configured
    // 2. There's debounced input
    // 3. Data isn't fully cached
    // 4. The input is NOT just the selected value's label (user actually typed something different)
    const shouldApplySearchParams = !!(
        apiSource?.searchColumns?.length &&
        debouncedInputValue &&
        !allDataLoaded &&
        !isInputMatchingSelection
    );

    const { data, isLoading, isFetching } = apiClient.useGet({
        depth: apiSource?.depth || [0],
        sort: apiSource?.sort ?? (apiSource?.distinctColumn ? [{
            column: apiSource.distinctColumn,
            order: 'asc'
        }] : undefined),
        params: {
            pageSize: resolvedPageSize,
            count: 1,
            ...apiSource?.params,
            ...(shouldApplySearchParams
                ? { or: createSearchParams(apiSource.searchColumns, debouncedInputValue, apiSource.searchOperator) }
                : undefined
            )
        },
        distinctColumn: apiSource?.distinctColumn || undefined,
        // Disable API calls when:
        // 1. The component is using a static array instead of an API source
        // 2. We already have all data loaded and can filter locally
        // 3. The component is disabled
        // Note: We no longer block API calls when a selection exists - users need to be able
        // to change their selection and see other options without clearing first
        enabled: Boolean(apiSource) && !allDataLoaded && !props.disabled
    });

    // Decide caching behavior based ONLY on the INITIAL unfiltered API response
    // If the initial response returns all records (length === totalRecords),
    // we cache and stop subsequent API calls. Otherwise, we always query the API.
    useEffect(() => {
        if (!data || !apiSource || allDataLoaded) return;
        if (initialApiEvaluatedRef.current) return;

        // Only evaluate the very first, unfiltered request
        // This includes when debouncedInputValue matches the selection (we skip search params in that case)
        const isInitialUnfiltered = !debouncedInputValue || isInputMatchingSelection;
        if (!isInitialUnfiltered) return;

        const currentCount = data.recordsCount;
        const totalCount = data.recordsTotal;

        if (typeof totalCount === 'number' && currentCount === totalCount) {
            setAllDataLoaded(true);
            setCachedData(data.data as unknown as T[]);
        }

        // We only make this decision once per data source
        initialApiEvaluatedRef.current = true;
    }, [data, apiSource, allDataLoaded, debouncedInputValue, isInputMatchingSelection]);

    const options = (() => {
        let opts: T[] = [];

        if (dataArray) {
            // Static array data source
            opts = dataArray;
        } else if (allDataLoaded) {
            // Use cached data and apply local filtering
            opts = cachedData;

            // Apply search filtering locally only when user has typed or no selection exists
            const shouldApplyLocalSearch = (
                !!inputValue &&
                !!apiSource?.searchColumns?.length &&
                (hasUserTyped || !props.value)
            );
            if (shouldApplyLocalSearch) {
                opts = opts.filter(option => {
                    return apiSource.searchColumns.some(column => {
                        const value = (option as Record<string, unknown>)[column];
                        return matchesFormSelectSearchValue(value, inputValue);
                    });
                });
            }

            // Apply local filter if provided
            if (apiSource?.localFilter) {
                opts = opts.filter(apiSource.localFilter);
            }
        } else {
            // Use API data (original behavior)
            opts = (data?.data as T[]) || [];
            if (data?.data && apiSource?.localFilter) {
                opts = (data.data as T[]).filter(apiSource.localFilter);
            }
        }

        // Deduplicate options based on getOptionKey if provided
        if (props.getOptionKey && opts.length > 0) {
            const seen = new Set();
            opts = opts.filter(option => {
                const key = props.getOptionKey!(option);
                if (key == null) return true; // Don't dedup items without valid keys
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        }

        if (sortOptions && props.getOptionLabel) {
            opts = sortOptionsByLabel(opts, props.getOptionLabel, props.groupBy);
        }

        return opts;
    })();

    const callerFilterOptions = props.filterOptions;
    const callerGetOptionLabel = props.getOptionLabel;

    const applyCallerFilterOptions = useCallback((sourceOptions: T[], searchValue: string): T[] => {
        if (!dataArray) return sourceOptions;

        const getLabel = callerGetOptionLabel || ((option: T) => String(option));
        if (callerFilterOptions) {
            return callerFilterOptions(sourceOptions, {
                inputValue: searchValue,
                getOptionLabel: getLabel,
            } as Parameters<NonNullable<typeof callerFilterOptions>>[1]);
        }

        const searchTerm = searchValue.trim().toLowerCase();
        if (!searchTerm) return sourceOptions;

        return sourceOptions.filter((option) => getLabel(option).toLowerCase().includes(searchTerm));
    }, [dataArray, callerFilterOptions, callerGetOptionLabel]);

    // Determine loading state - show loading when:
    // 1. Initial load (isLoading)
    // 2. When actively searching with API source and we don't have all data cached
    const isSelectLoading = apiSource ?
        (
            isLoading ||
            (isFetching && !allDataLoaded) ||
            (!!inputValue && inputValue !== debouncedInputValue && !allDataLoaded) ||
            // Mask early "N/A-only" results until data context stabilizes
            (!allDataLoaded && !!props.getOptionLabel && options.length > 0 && options.every(o => props.getOptionLabel!(o) === 'N/A'))
        ) :
        false;

    // Hide options while loading or while we only have placeholder "N/A" rows during initial fetch
    const optionsForUI: T[] = (apiSource && (
        isLoading || (isFetching && !allDataLoaded) ||
        (!allDataLoaded && !!props.getOptionLabel && options.length > 0 && options.every(o => props.getOptionLabel!(o) === 'N/A'))
    )) ? [] as T[] : options;

    // One-shot auto-select per mount when there's exactly one option
    const didAutoSelectRef = React.useRef<boolean>(false);
    useEffect(() => {
        if (!autoSelectSingleOption) return;
        if (didAutoSelectRef.current) return;
        if (props.value) return; // do not override an existing selection
        if (props.disabled) return; // respect disabled state
        if (isSelectLoading) return; // wait until loading settles (also masks N/A-only)
        if (hasUserTyped) return; // user has interacted; do not auto-select

        // Only auto-select on the initial, unfiltered state (no user input)
        if (inputValue && inputValue.trim() !== '') return;

        if (options.length === 1) {
            const soleOption = options[0];
            props.onChange?.(null as unknown as ReactTypes.SyntheticEvent, soleOption as unknown as T, 'selectOption' as AutocompleteChangeReason);
            didAutoSelectRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.length, autoSelectSingleOption, props.value, props.disabled, isSelectLoading, inputValue, hasUserTyped]);

    const handleInputChange: NonNullable<typeof onInputChange> = useCallback((event: React.SyntheticEvent, newInputValue: string, reason: AutocompleteInputChangeReason) => {
        // On mobile, Autocomplete doesn't call this (onInputChange={undefined}).
        // Only the drawer's handleDrawerInputChange calls it directly.
        if (onInputChange) {
            onInputChange(event, newInputValue, reason);
        }

        if (reason === 'input' && newInputValue.trim() !== '') {
            setHasUserTyped(true);
        }

        setInputValue(newInputValue);
    }, [onInputChange]);

    // Forward onChange as-is
    const handleChange: NonNullable<typeof props.onChange> = (event, newValue, reason, details) => {
        props.onChange?.(event, newValue, reason, details);
    };
    const noopHandleChange = useCallback<NonNullable<typeof props.onChange>>(() => undefined, []);

    // Mobile drawer handlers
    const handleDrawerOpen = useCallback(() => {
        setDrawerSearchValue('');
        setInputValue('');
        setHasUserTyped(false);
        setDrawerOpen(true);
    }, []);

    const selectedValue = props.value;
    const getOptionLabel = props.getOptionLabel;

    const handleDrawerClose = useCallback(() => {
        setDrawerOpen(false);
        setDrawerSearchValue('');
        // Reset inputValue to selected label (or '') so FormSelect's
        // data pipeline returns to the unfiltered state
        if (selectedValue && getOptionLabel) {
            setInputValue(getOptionLabel(selectedValue));
        } else {
            setInputValue('');
        }
        setHasUserTyped(false);
    }, [selectedValue, getOptionLabel]);

    const handleDrawerInputChange = useCallback((value: string) => {
        setDrawerSearchValue(value);
        // Drive FormSelect's data pipeline through the same path as desktop
        handleInputChange(null as unknown as React.SyntheticEvent, value, 'input');
    }, [handleInputChange]);

    const { onChange: propsOnChange } = props;
    const handleDrawerSelect = useCallback((option: T | null) => {
        const reason: AutocompleteChangeReason = option ? 'selectOption' : 'clear';
        propsOnChange?.(null as unknown as ReactTypes.SyntheticEvent, option as T, reason);
    }, [propsOnChange]);

    // Enhanced renderInput that automatically handles "..." suffix
    const enhancedRenderInput = props.renderInput ? (params: AutocompleteRenderInputParams): ReactNode => {
        const originalTextField = props.renderInput(params);

        // Type guard to check if this is a React element with TextField-like props
        if (React.isValidElement<TextFieldProps>(originalTextField)) {
            const elementProps = originalTextField.props;
            const originalLabel = elementProps.label as string;
            const originalPlaceholder = elementProps.placeholder!;

            // Capture label/placeholder for the mobile drawer
            drawerLabelRef.current = originalLabel || '';
            drawerPlaceholderRef.current = originalPlaceholder || '';

            const hasSelection = !!props.value;
            const nextLabel = hasSelection || !originalLabel ? originalLabel : `${originalLabel}...`;
            const nextPlaceholder = hasSelection || !originalPlaceholder ? originalPlaceholder : `${originalPlaceholder}...`;

            const mobileProps: Partial<TextFieldProps> = isMobileScreen ? {
                slotProps: {
                    ...elementProps.slotProps,
                    input: {
                        ...(elementProps.slotProps?.input as Record<string, unknown> || {}),
                        readOnly: true
                    }
                },
                onMouseDown: !props.disabled ? (e: React.MouseEvent) => { e.preventDefault(); handleDrawerOpen(); } : undefined
            } : {};

            return React.cloneElement(originalTextField, {
                ...elementProps,
                label: nextLabel,
                placeholder: nextPlaceholder,
                ...mobileProps
            } as TextFieldProps);
        }

        return originalTextField;
    } : () => null;
    //#endregion

    //########## MARKDOWN ##########
    // #region MARKDOWN

    // On mobile the Autocomplete is purely decorative (readOnly, open: false, options: []).
    // Show the selected option's label instead of the internal inputValue so that
    // Autocomplete reset events cannot corrupt the drawer's search/data pipeline.
    const mobileDisplayValue = (props.value && props.getOptionLabel)
        ? props.getOptionLabel(props.value)
        : '';
    const mobileOptionsForUI = isMobileScreen
        ? applyCallerFilterOptions(optionsForUI, drawerSearchValue)
        : optionsForUI;

    return <>
        <Autocomplete {...props}
            options={isMobileScreen ? [] : optionsForUI}
            {...(isMobileScreen ? {
                open: false,
                disableClearable: true as const,
                popupIcon: null,
            } : {})}
            blurOnSelect={true}
            onChange={isMobileScreen ? noopHandleChange : handleChange}
            loading={isMobileScreen ? false : isSelectLoading}
            filterOptions={apiSource ? (x) => x : callerFilterOptions}
            inputValue={isMobileScreen ? mobileDisplayValue : inputValue}
            onInputChange={isMobileScreen ? undefined : handleInputChange}
            loadingText="Loading..."
            noOptionsText="No options"
            renderInput={enhancedRenderInput}
        />
        {isMobileScreen && (
            <MobileSelectDrawer<T>
                open={drawerOpen}
                onClose={handleDrawerClose}
                onOpen={handleDrawerOpen}
                inputValue={drawerSearchValue}
                onInputChange={handleDrawerInputChange}
                label={drawerLabelRef.current}
                placeholder={drawerPlaceholderRef.current}
                options={mobileOptionsForUI}
                getOptionLabel={props.getOptionLabel || ((o) => String(o))}
                getOptionKey={props.getOptionKey}
                isOptionEqualToValue={props.isOptionEqualToValue}
                groupBy={mobileGroupBy || props.groupBy}
                getOptionSecondaryText={getOptionSecondaryText}
                value={props.value ?? null}
                onChange={handleDrawerSelect}
                loading={isSelectLoading}
                disableClearable={Boolean(props.disableClearable)}
            />
        )}
    </>;
    //#endregion
}
